<template>
  <div
    id="video-transcoder"
    class="relative height-100"
    :class="userAgent && userAgent.os && userAgent.os.name && userAgent.os.name.toLowerCase()"
  >
    <resize-observer class="display-none" @notify="handleTranscoderResize" />
    <loading-screen
      :loading="loading"
      :loadings="loading.loadings"
      :toggle-loading="toggleLoading"
      :update-loading-by-index="updateLoadingByIndex"
    ></loading-screen>
    <transition name="fade">
      <v-card
        v-if="loaded"
        flat
        class="responsive transcoder-card"
        :class="{'no-pointer-events': loading.enabled()}"
        :width="cardWidth"
        :height="cardHeight"
      >
        <v-toolbar
          color="primary"
          dark
          flat
          :extended="!hideTabs"
        >
          <v-layout justify-space-between align-center>
            <v-flex xs8>
              <v-layout class="width-100" justify-start align-center v-if="loaded">
                <Tooltip
                  v-if="(
                    ['master_school_admin', 'school_admin', 'premium_teacher'].includes(hostingLog.type)
                    && !hostingLog.hasLimitedVideoUploadAccess
                  )"
                  content-class="video-transcoder-storage-tooltip"
                  right
                  attach=".usage-wrapper"
                  :z-index="99999999999"
                >
                  <div class="usage-wrapper">
                    <v-progress-linear
                      class="mb-1"
                      no-flex
                      v-model="hostingLog.rawPercentage"
                      color="white"
                      :style="storageSliderStyles"
                    ></v-progress-linear>
                    <div
                      :aria-label="storageAriaLabel"
                      tabindex="0"
                      ref="storageLabel"
                      class="caption"
                      :class="hostingLog.decimal >= 0.9 ? 'white--text' : 'text--faded-light'"
                    >
                      <v-icon
                        v-show="hostingLog.decimal >= 0.9"
                        color="white"
                        small
                        class="mr-1"
                      >
                        warning
                      </v-icon>
                      <span
                        v-if="hostingLog.totalHostingMinutes !== 0 && typeof hostingLog.totalHostingMinutes === 'number'"
                        :class="hostingLog.decimal >= 0.9 ? 'white--text' : 'text--faded-light'"
                      >
                        {{hostingLog.percentage}}% of storage used.
                      </span>
                      <span
                        v-else
                        :class="hostingLog.decimal >= 0.9 ? 'white--text' : 'text--faded-light'"
                      >
                        {{Number(hostingLog.currentHostingMinutes).toLocaleString()}}m used.
                      </span>
                    </div>
                  </div>
                  <template v-slot:text>
                    <v-layout column justify-center>
                      <v-layout class="width-100" justify-space-between>
                        <div class="white--text">Current usage:</div>
                        <div class="white--text">{{Number(hostingLog.currentHostingMinutes).toLocaleString()}}m</div>
                      </v-layout>
                      <v-layout class="width-100" justify-space-between>
                        <div class="white--text">Usage limit:</div>
                        <div class="white--text" v-if="hostingLog.totalHostingMinutes !== 0 && typeof hostingLog.totalHostingMinutes === 'number'">{{Number(hostingLog.totalHostingMinutes).toLocaleString()}}m</div>
                        <div class="white--text" v-else>
                          Unlimited
                        </div>
                      </v-layout>
                      <v-layout class="width-100" v-if="hostingLog.totalHostingMinutes !== 0 && typeof hostingLog.totalHostingMinutes === 'number'" justify-space-between>
                        <div class="white--text">Percent used:</div>
                        <div class="white--text">{{hostingLog.rawPercentage}}%</div>
                      </v-layout>
                    </v-layout>
                  </template>
                </Tooltip>
              </v-layout>
            </v-flex>
            <v-flex v-show="showCloseButton" xs4 class="text-xs-center">
              <v-layout justify-end align-center>
                <v-btn aria-label="Close record/upload modal" icon @click="verifyExitPermissions">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-tabs
            v-if="!hideTabs"
            slot="extension"
            v-model="cardTab"
            color="primary"
            grow
            slider-color="white"
          >
            <v-tab
              aria-label="Record tab"
              @focus.native="handleTabFocusBlur($event, 0)"
              @blur.native="handleTabFocusBlur"
              :tabindex="loading.enabled() ? -1 : 0"
              :disabled="loading.enabled()"
              :key="0"
            >
              <v-icon class="mr-1">videocam</v-icon>
              <span class="ml-1 white--text">Record</span>
            </v-tab>
            <v-tab
              aria-label="Upload tab"
              @focus.native="handleTabFocusBlur($event, 1)"
              @blur.native="handleTabFocusBlur"
              :tabindex="loading.enabled() ? -1 : 0"
              :disabled="loading.enabled()"
              :key="1"
            >
              <template>
                <v-icon class="mr-1">file_upload</v-icon>
                <span class="ml-1 white--text">Add new video</span>
              </template>
            </v-tab>
          </v-tabs>
        </v-toolbar>
        <v-card-text class="relative card-content full-height">
          <video-record
            v-show="!hideTabs && cardTab === 0"
            v-bind="$props"
            @success="$emit('success', $event)"
            @error="$emit('error', $event)"
          ></video-record>
          <VideoUploadUppy
            v-if="(isPremiumTeacher || specialHostingType) && (cardTab === 1 || hideTabs)"
            v-bind="$props"
            :video-host="specialHostingType"
            :hosting-log="hostingLog"
            @success="$emit('success', $event)"
            @error="$emit('error', $event)"
          />
          <video-upload
            v-show="(!isPremiumTeacher && !specialHostingType) && (cardTab === 1 || hideTabs)"
            v-bind="$props"
            :hosting-log="hostingLog"
            :max-size="hostingLog && hostingLog.hasLimitedVideoUploadAccess ? '300MB' : '5000MB'"
            @success="$emit('success', $event)"
            @error="$emit('error', $event)"
          ></video-upload>
        </v-card-text>
      </v-card>
      <v-layout
        v-else
        class="initial-loading-screen"
        justify-center
        align-center
      >
        <v-progress-circular
          :size="compact ? 100 : 150"
          indeterminate
          color="primary"
        >
        </v-progress-circular>
      </v-layout>
    </transition>
  </div>
</template>

<script>
/* eslint-disable */
import 'ua-parser-js';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import EventBus from '@snippets/js/eventBus';
import videoTranscoderStore from './store';
import VideoUpload from './components/VideoUpload.vue';
import VideoUploadUppy from './components/VideoUploadUppy.vue';
import VideoRecord from './components/VideoRecord.vue';
import LoadingScreen from './components/LoadingScreen.vue';
import Tooltip from '@snippets/vue/Tooltip.vue';
import VideoTranscoderService from './services/VideoTranscoderService';
import {
  hexToRGBA,
} from '@utilities';
const parser = require('ua-parser-js');
import createFocusTrap from 'focus-trap';

export default {
  name: 'video-transcoder',
  components: {
    VideoUpload,
    VideoUploadUppy,
		VideoRecord,
    LoadingScreen,
    Tooltip,
  },
  props: {
    apiUrl: {
      type: String,
      required: true
    },
    token: {
      type: String,
      required: true,
    },
    teacherId: {
      type: Number,
      required: true,
    },
    startingKey: {
      type: String,
      validator: sk => ['webcam', 'screen', 'upload'].includes(sk),
    },
    studentId: {
      type: Number,
      default: null
    },
    bulbAttemptId: {
      type: Number,
      default: null
    },
    questionId: {
      type: Number,
      default: null
    },
    discussionId: {
      type: Number,
      default: null
    },
    discussionAttemptInteractionId: {
      type: Number,
      default: null
    },
    bulbId: {
      type: Number,
      default: null
    },
    lessonId: {
      type: Number,
      default: null
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    maxSize: {
      type: String,
      default: '5000MB'
    },
    maxDuration: {
      type: Number,
      default: 3600
    },
    showCloseButton: {
      type: [String, Boolean],
      default: true,
      validator: scb => [
        true,
        false,
        'left',
      ].includes(scb)
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      testTab: 0,
      transcoderCardTab: 0,
      loaded: false,
      interval: null,
      storageSliderStyles: {},
    }
  },
  computed: {
    ...mapState('videoTranscoder', [
      'userAgent',
      'loading',
      'hostingLog',
      'keyboardFocus'
    ]),
    ...mapGetters('videoTranscoder', [
      'compact',
      'mobile'
    ]),
    url() {
      return this.apiUrl[this.apiUrl.length - 1] === '/' ? this.apiUrl : `${this.apiUrl}/`;
    },
    cardTab: {
      get() {
        return this.$store.getters['videoTranscoder/cardTab'];
      },
      set(cardTab) {
        this.updateCardTab(cardTab);
      }
    },
    cardWidth() {
    	return (this.width) ? this.width : '100%';
		},
		cardHeight() {
			return (this.height) ? this.height : '100%';
    },
    isPremiumTeacher() {
      return _.get(this, 'hostingLog.type') === 'premium_teacher';
    },
    specialHostingType() {
      const hostingLevel = _.get(this, 'hostingLog.schoolHostingLevel');
      if (hostingLevel === 3) return 'vimeo';
      else if (hostingLevel === 4) return 'cloudflare';
      else if (
        hostingLevel === 5
        || this.isPremiumTeacher
      ) return 'digitalocean';
      return false;
    },
    hideTabs() {
      return (
        this.mobile
        || this.specialHostingType
      );
    },
    storageAriaLabel() {
      let label = '';
      if (
        this.hostingLog.totalHostingMinutes !== 0
        && typeof this.hostingLog.totalHostingMinutes === 'number'
      ) {
        if (this.hostingLog.decimal > 0.9) label += 'Warning! ';
        label += `You have used ${this.hostingLog.percentage}% of your allotted storage. You have recorded ${Number(this.hostingLog.currentHostingMinutes).toLocaleString()} minute${Number(this.hostingLog.currentHostingMinutes) === 1 ? '' : 's'} of video. Your limit is ${Number(this.hostingLog.totalHostingMinutes).toLocaleString()} minutes.`;
      } else {
        label = `You have recorded ${Number(this.hostingLog.currentHostingMinutes).toLocaleString()} minute${Number(this.hostingLog.currentHostingMinutes) === 1 ? '' : 's'} of video.`
      }
      return label;
    },
  },
  methods: {
    ...mapMutations('videoTranscoder', [
      'updateUserAgent',
      'updateTranscoderEnvironment',
      'updateKeyboardFocus',
      'updateTranscoderSize',
      'updateCardTab',
      'updateHostingLog',
      'updateLoadingByIndex',
      'toggleLoading',
    ]),
    ...mapActions('videoTranscoder', [
      'constructTranscoderEnvironment'
    ]),
    handleTranscoderResize() {
      this.updateTranscoderSize(this.$el.clientWidth);
    },
    updateUserVariables() {
      VideoTranscoderService.url = this.url;
      VideoTranscoderService.setupAjax(this.token);
      this.constructTranscoderEnvironment(this.$props)
      .catch(msg => {
        throw new Error(msg);
      });
    },
    updatePulseAnimations(branding) {
      const active = this.$vuetify.theme.primary;
      const base = this.$vuetify.theme.secondary;
      const inactive = this.$vuetify.theme.tertiary;
      const success = this.$vuetify.theme.success;
      [active, base, inactive, success]
        .filter(color => color !== undefined && color !== null)
        .forEach((hex, index) => {
        let rgba = hexToRGBA(hex);
        let name;
        if (index === 0) name = 'primary';
        else if (index === 1) name = 'secondary';
        else if (index === 2) name = 'tertiary';
        else name = 'success';
        this.addStyleString(`
          @keyframes pulse-${name} {
            0% {
              box-shadow: 0 0 0 0 ${rgba.slice(0, -2)}0.9);
            }
            70% {
              box-shadow: 0 0 0 10px ${rgba.slice(0, -2)}0);
            }
            100% {
              box-shadow: 0 0 0 0 ${rgba.slice(0, -2)}0);
            }
          }

          .pulse-${name} {
            animation: pulse-${name} 2s infinite;
          }
        `);
      });
    },
    addStyleString(str) {
      const node = document.createElement('style');
      node.innerHTML = str;
      document.body.appendChild(node);
    },
    handleTabFocusBlur(e, num) {
      let handleKeyUp = e => {
        if ([13, 32].includes(e.keyCode)) {
          this.updateCardTab(num);
        }
      }
      if (e.type === 'focus') {
        e.target.addEventListener('keyup', handleKeyUp);
      } else if (e.type === 'blur') {
        e.target.removeEventListener('keyup', handleKeyUp);
      }
		},
		verifyExitPermissions() {
			EventBus.$emit('verify-exit-permissions', (bool, text = 'Your video is currently being transcoded.') => {
				if (bool) return this.$emit('exit');
				if (window.confirm(`${text} Exit anyways?`)) this.$emit('exit');
			});
    },
    handleKeyDown(e) {
      if (e.keyCode === 9) this.updateKeyboardFocus(true);
      else if (e.keyCode === 27) this.verifyExitPermissions();
    },
    handleMouseDown() {
      this.updateKeyboardFocus(false);
    },
    async initKeys() {
      window.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('mousedown', this.handleMouseDown);
      await this.$nextTick();
      window.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('mousedown', this.handleMouseDown);
    },
    async getHostingLog() {
      try {
        const { data: hostingLog } = await VideoTranscoderService.getHostingLog(this.teacherId);
        this.updateHostingLog(hostingLog);
        this.loaded = true;
        await this.$nextTick();
        this.updateTranscoderSize(this.$el.clientWidth);
      } catch(e) {
        this.$emit('exit', 'Unable to get hosting log at this time.');
      }
    }
  },
  watch: {
    async loaded(n) {
      await this.$nextTick();
      this.focusTrap = createFocusTrap(this.$el, {
        escapeDeactivates: false
      });
      if (this.$refs.storageLabel) {
        this.$set(this, 'storageSliderStyles', {
          width: `${this.$refs.storageLabel.getBoundingClientRect().width}px`
        });
      }
    },
    keyboardFocus(n) {
      if (this.focusTrap) this.focusTrap[n ? 'activate' : 'deactivate']();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousedown', this.handleMouseDown);
    if (this.focusTrap) this.focusTrap.deactivate();
  },
  beforeCreate() {
    if (!_.get(this, '$store.state.videoTranscoder')) this.$store.registerModule('videoTranscoder', videoTranscoderStore);
  },
  mounted() {
    try {
      // This Vuex registration is in both created() and mounted() because of hot-reloading during development.
      if (!_.get(this, '$store.state.videoTranscoder')) this.$store.registerModule('videoTranscoder', videoTranscoderStore);
      this.updateUserVariables();
      let ua = new parser.UAParser().getResult();
      this.updateUserAgent(ua);
      this.updatePulseAnimations();
      if (this.startingKey && !this.hideTabs) this.updateCardTab(this.startingKey === 'upload' ? 1 : 0);
      else if (this.hideTabs) this.updateCardTab(1);
      this.initKeys();
      this.getHostingLog();
    } catch(e) {
      this.$emit('exit', 'Unable to initialize the video transcoder at this time.');
    }
  },
};
</script>

<style lang="scss">
.mobile-tab {
  color: white;
  .v-tabs__item {
    color: white !important;
    opacity: 1 !important;
  }
}

.max-width-100 {
  max-width: 100% !important;
}
</style>

<style lang="scss" scoped>
.video-transcoder-storage-tooltip {
  min-width: 200px !important;
  top: 3px !important;
}

.responsive {
  max-width: 100%;
  max-height: 100%;
}

.underline {
  text-decoration: underline !important;
}

.full-height {
  height: 100%;
}

.height-100 {
  height: 100%;
}

.full-width {
  width: 100%;
}

.width-100 {
  width: 100%;
}

.relative {
  position: relative;
}

.text--faded-light {
  color: rgba(255,255,255,0.7);
}

.display-none {
  display: none;
}

.initial-loading-screen {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.flex-all-center,
.v-content__wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-content__wrap {
  flex-direction: column;
}

.filepond--browser {
  height: 100% !important;
  opacity: 1 !important;
  border-radius: 0.5em;
}

button,
a,
input.filepond--browser,
.v-tabs__div {
  &:focus {
    outline: 2px solid #FD9727 !important;
  }
}

.v-window__container {
  height: auto !important;
}

.v-tabs__div {
  &:focus {
    outline-offset: -2px;
  }
}

.pulse-button {
  .v-btn__content {
    position: static !important;
  }
}

.transcoder-card {
  overflow-y: auto;
  border-radius: 0;
  display: flex;
  flex-direction: column;

  &.no-pointer-events * {
    pointer-events: none !important;
  }

  &.no-pointer-events .loading-screen * {
    pointer-events: all !important;
  }

  .loading-screen #loading-screen-cancel {
    pointer-events: all !important;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .150s linear;
}
.fade-enter-active {
  transition-delay: .150s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-time-enter-active {
  transition: all .150s linear;
}
.fade-time-leave-active {
  transition: all .150s linear;
}
.fade-time-enter, .fade-time-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.fade-right-enter-active {
  transition: translate .150s linear, opacity .150s ease-in;
}
.fade-right-leave-active {
  transition: translate .150s linear, opacity .150s ease-out;
}
.fade-right-enter, .fade-right-leave-to {
  opacity: 0;
  transform: translateX(-140%);
}

.fade-left-enter-active {
  transition: translate .150s linear, opacity .150s ease-in;
}
.fade-left-leave-active {
  transition: translate .150s linear, opacity .150s ease-out;
}
.fade-left-enter, .fade-left-leave-to {
  opacity: 0;
  transform: translateX(140%);
}

[data-whatintent='mouse'] #video-transcoder *:focus {
  outline: none !important;
}
</style>
